import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import type { DocumentDetailDto } from '@/types/document';
import type { BrowsableDocument } from './useDocumentBrowserStore';
import { DocumentStatusBadge } from './DocumentStatusBadge';
import {
  formatChunkProgress,
  formatDateTime,
  formatDocumentTypeLabel,
  formatFileSize,
  formatLanguageLabel,
  formatPageCountLabel,
  formatSourceOrigin,
  formatVersionLabel,
  getDocumentDisplayTitle,
  getSourceKindLabel,
  getUploaderLabel,
} from './documentBrowserUtils';

interface DocumentDetailsDialogProps {
  document: BrowsableDocument | null;
  detail: DocumentDetailDto | null;
  loading: boolean;
  onOpenChange: (open: boolean) => void;
  onOpenPreview: (document: BrowsableDocument) => void;
  onDownload: (document: BrowsableDocument) => void;
}

function MetadataItem({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border/60 bg-muted/20 p-3">
      <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{label}</p>
      <p className="mt-1 break-words text-sm font-medium text-foreground">{value}</p>
    </div>
  );
}

function DetailSection({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-lg border border-border/60 bg-card/70 p-4">
      <div className="mb-3 space-y-1">
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
      {children}
    </section>
  );
}

function getReviewState(document: BrowsableDocument): string {
  switch (document.status) {
    case 'AwaitingConfirmation':
      return 'Review offen';
    case 'Completed':
    case 'CompletedWithoutEmbeddings':
      return 'Kein offener Review';
    case 'Failed':
    case 'PartiallyFailed':
    case 'EmptyDocument':
      return 'Review durch Problemstatus blockiert';
    default:
      return 'Noch in Verarbeitung';
  }
}

function getClassificationState(document: BrowsableDocument): string {
  if (
    document.displayTitle
    || document.pageCount
    || document.documentType
    || document.language
    || document.machineModelId
    || document.machineSeries
    || document.machineManufacturer
    || document.machineCategory
  ) {
    return 'Persistierte Viewer-Metadaten vorhanden';
  }

  return 'Noch keine Klassifikation persistiert';
}

export function DocumentDetailsDialog({
  document,
  detail,
  loading,
  onOpenChange,
  onOpenPreview,
  onDownload,
}: DocumentDetailsDialogProps) {
  const activeDocument = detail ?? document;

  return (
    <Dialog open={!!document} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] w-[min(92vw,960px)] max-w-3xl flex-col overflow-hidden p-0" aria-describedby={undefined}>
        <div className="border-b border-border/60 bg-muted/20 px-5 py-4 sm:px-6">
          <DialogHeader className="space-y-3">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="outline">{activeDocument ? getSourceKindLabel(activeDocument.sourceKind) : 'Dokument'}</Badge>
                  {activeDocument && <DocumentStatusBadge status={activeDocument.status} />}
                </div>
                <DialogTitle className="max-w-2xl text-xl font-semibold leading-tight sm:text-2xl">
                  {activeDocument ? getDocumentDisplayTitle(activeDocument) : 'Dokumentdetails'}
                </DialogTitle>
                <DialogDescription className="max-w-2xl text-sm text-muted-foreground">
                  {activeDocument
                    ? `${activeDocument.sourceLabel ?? 'Manueller Upload'} · ${getUploaderLabel(activeDocument)} · ${formatVersionLabel(activeDocument)}${activeDocument.displayTitle ? ` · ${activeDocument.fileName}` : ''}`
                    : 'Dokumentinformationen werden geladen.'}
                </DialogDescription>
              </div>

              {document && (
                <div className="flex flex-wrap gap-2">
                  <Button variant="secondary" onClick={() => onOpenPreview(document)}>
                    Vorschau
                  </Button>
                  <Button variant="outline" onClick={() => onDownload(document)}>
                    Herunterladen
                  </Button>
                </div>
              )}
            </div>
          </DialogHeader>
        </div>

        <div className="min-h-0 overflow-y-auto px-5 py-4 sm:px-6 sm:py-5">
          <div className="grid gap-5 2xl:grid-cols-[minmax(0,1fr)_260px]">
          <div className="min-w-0 space-y-4">
            {loading && !activeDocument ? (
              <div className="grid gap-4">
                {Array.from({ length: 4 }).map((_, index) => (
                  <Skeleton key={index} className="h-28 rounded-lg" />
                ))}
              </div>
            ) : activeDocument ? (
              <>
                <DetailSection
                  title="Basisdaten"
                  description="Technische und organisatorische Kerndaten des Dokuments im Viewer."
                >
                  <div className="grid gap-3 sm:grid-cols-2">
                    <MetadataItem label="Viewer-Titel" value={getDocumentDisplayTitle(activeDocument)} />
                    <MetadataItem label="Dateiname" value={activeDocument.fileName} />
                    <MetadataItem label="Quelle" value={activeDocument.sourceLabel ?? 'Manueller Upload'} />
                    <MetadataItem label="Quelladresse" value={formatSourceOrigin(activeDocument)} />
                    <MetadataItem label="Uploader" value={getUploaderLabel(activeDocument)} />
                    <MetadataItem label="Hochgeladen" value={formatDateTime(activeDocument.createdAt)} />
                    <MetadataItem label="Abgeschlossen" value={formatDateTime(activeDocument.completedAt)} />
                    <MetadataItem label="Version" value={formatVersionLabel(activeDocument)} />
                    <MetadataItem label="Dateityp" value={activeDocument.contentType} />
                  </div>
                </DetailSection>

                <DetailSection
                  title="Dokumentklassifikation"
                  description="Persistierte Viewer-Metadaten für Typ, Sprache und Einordnung des Dokuments."
                >
                  <div className="grid gap-3 sm:grid-cols-2">
                    <MetadataItem label="Seiten" value={formatPageCountLabel(activeDocument.pageCount)} />
                    <MetadataItem label="Dokumenttyp" value={formatDocumentTypeLabel(activeDocument.documentType)} />
                    <MetadataItem label="Sprache" value={formatLanguageLabel(activeDocument.language)} />
                    <MetadataItem label="Klassifikationsstatus" value={getClassificationState(activeDocument)} />
                    <MetadataItem label="Viewer-Status" value={getReviewState(activeDocument)} />
                  </div>
                </DetailSection>

                <DetailSection
                  title="Maschinenkontext"
                  description="Dokumentbezogener Maschinenbezug aus Registry-Link und Viewer-Snapshot."
                >
                  <div className="grid gap-3 sm:grid-cols-2">
                    <MetadataItem label="Maschine" value={activeDocument.machineLabel ?? 'Nicht zugeordnet'} />
                    <MetadataItem label="Maschinen-ID" value={activeDocument.machineModelId ?? 'Kein Registry-Link'} />
                    <MetadataItem label="Hersteller" value={activeDocument.machineManufacturer ?? 'Nicht persistiert'} />
                    <MetadataItem label="Serie" value={activeDocument.machineSeries ?? 'Nicht persistiert'} />
                    <MetadataItem label="Kategorie" value={activeDocument.machineCategory ?? 'Nicht persistiert'} />
                  </div>
                </DetailSection>

                <DetailSection
                  title="Gueltigkeit"
                  description="Review- und Gueltigkeitsinformationen für spaetere Governance- und Compliance-Felder."
                >
                  <div className="grid gap-3 sm:grid-cols-2">
                    <MetadataItem label="Reviewstatus" value={getReviewState(activeDocument)} />
                    <MetadataItem label="Letztes Viewer-Ereignis" value={formatDateTime(activeDocument.completedAt ?? activeDocument.createdAt)} />
                    <MetadataItem label="Gueltig ab" value="Noch nicht persistiert" />
                    <MetadataItem label="Gueltig bis" value="Noch nicht persistiert" />
                  </div>
                </DetailSection>

                <DetailSection
                  title="Tags & Labels"
                  description="Fachliche Schlagwoerter folgen erst mit dokumentbezogener Tag-Persistenz. Bis dahin werden nur abgeleitete Hinweise gezeigt."
                >
                  <div className="space-y-3">
                    <div className="flex flex-wrap gap-2">
                      <Badge variant="outline">Noch keine Tags</Badge>
                      {activeDocument.documentType && <Badge variant="secondary">Typ: {formatDocumentTypeLabel(activeDocument.documentType)}</Badge>}
                      {activeDocument.language && <Badge variant="secondary">Sprache: {formatLanguageLabel(activeDocument.language)}</Badge>}
                      {activeDocument.machineLabel && <Badge variant="secondary">Maschine: {activeDocument.machineLabel}</Badge>}
                      {activeDocument.machineCategory && <Badge variant="secondary">Kategorie: {activeDocument.machineCategory}</Badge>}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Diese Labels sind derzeit aus vorhandenen Viewer-Metadaten abgeleitet und noch keine eigenstaendig persistierten Dokument-Tags.
                    </p>
                  </div>
                </DetailSection>
              </>
            ) : null}

            {activeDocument?.errorMessage ? (
              <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4">
                <p className="text-xs uppercase tracking-[0.18em] text-destructive">Fehlermeldung</p>
                <p className="mt-2 text-sm text-foreground">{activeDocument.errorMessage}</p>
              </div>
            ) : (
              <div className="rounded-lg border border-border/60 bg-muted/10 p-4">
                <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Viewer-Hinweis</p>
                <p className="mt-2 text-sm leading-6 text-muted-foreground">
                  Dokumenttyp, Sprache und der Maschinenbezug werden aus persistierten Viewer-Metadaten gelesen.
                  Die Quelladresse wird für Connector-Dokumente direkt aus dem stabilen Connector-Pfad gelesen.
                  Gueltigkeit und eigenstaendige Dokument-Tags sind in der Oberflaeche jetzt sichtbar, aber fachlich noch nicht persistiert.
                </p>
              </div>
            )}
          </div>

          <div className="min-w-0 space-y-4">
            <div className="rounded-lg border border-border/60 bg-card/70 p-4">
              <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Ingest-Zusammenfassung</p>
              {activeDocument ? (
                <div className="mt-3 space-y-3 text-sm">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-muted-foreground">Viewer-Titel</span>
                      <span className="text-right font-medium text-foreground">{getDocumentDisplayTitle(activeDocument)}</span>
                    </div>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-muted-foreground">Dateigroesse</span>
                    <span className="font-medium text-foreground">{formatFileSize(activeDocument.fileSizeBytes)}</span>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-muted-foreground">Quelladresse</span>
                    <span className="max-w-[14rem] truncate text-right font-medium text-foreground" title={formatSourceOrigin(activeDocument)}>
                      {formatSourceOrigin(activeDocument)}
                    </span>
                  </div>
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-muted-foreground">Seiten</span>
                      <span className="font-medium text-foreground">{formatPageCountLabel(activeDocument.pageCount)}</span>
                    </div>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-muted-foreground">Technischer Status</span>
                    <span className="font-medium text-foreground">{activeDocument.status}</span>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-muted-foreground">Chunk-Fortschritt</span>
                    <span className="text-right font-medium text-foreground">{formatChunkProgress(activeDocument)}</span>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-muted-foreground">Chunk-Zeilen</span>
                    <span className="font-medium text-foreground">{detail?.chunks?.length ?? activeDocument.totalChunks}</span>
                  </div>
                </div>
              ) : (
                <Skeleton className="mt-3 h-24 rounded-lg" />
              )}
            </div>

            <div className="rounded-lg border border-border/60 bg-card/70 p-4">
              <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Naechste Schritte</p>
              <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
                <li>Vorschau oeffnen, um das Originaldokument direkt aus dem Viewer zu pruefen.</li>
                <li>Quelle und Status gemeinsam lesen: Business-Badge oben, technischer Pipeline-Status rechts.</li>
                <li>Filter auf Dokumenttyp, Sprache und Maschine nutzen, um die Viewer-Liste fachlich einzugrenzen.</li>
              </ul>
            </div>
          </div>
          </div>
        </div>

        <DialogFooter className="border-t border-border/60 px-5 py-4 sm:px-6">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Schliessen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}